import React, { useEffect, useState, useRef, useCallback } from 'react';

import {
  useNavigate,
  useLocation,
  Location as RouterLocation
} from 'react-router-dom';
import { addSearchParam } from '../../libs/url';

import {
  chooseComponent,
  removeSearchParamFromURL,
  processParamGroups
} from './utils';
import { IDialog, IDialogParamGroups } from './types';
import { UrlStateActionType, useUrlState, useUrlStateUpdater } from './url';

const DialogsContext = React.createContext<JSX.Element[]>([]);
const UpdaterContext = React.createContext(null);

export const ActiveDialogsProvider: React.FC = ({ children }) => {
  const [dialogs, setDialogs] = useState<JSX.Element[]>([]);
  /**
   * We gain performance improvements by splitting out
   * the setDialogs from the dialogs state. This way there will be less
   * unnecessary re-renders, especially for components that
   * only depend on the setDialogs function.
   */
  return (
    <DialogsContext.Provider value={dialogs}>
      <UpdaterContext.Provider value={setDialogs}>
        {children}
      </UpdaterContext.Provider>
    </DialogsContext.Provider>
  );
};

export function useActiveDialogs(): JSX.Element[] {
  const dialogs = React.useContext(DialogsContext);
  if (dialogs === undefined) {
    throw new Error(
      'useActiveDialogs must be used within an ActiveDialogsProvider'
    );
  }
  return dialogs;
}

export function useActiveDialogsUpdater(): (dialog: IDialog) => void {
  const urlStateDispatch = useUrlStateUpdater();
  const setDialogs =
    React.useContext<React.Dispatch<React.SetStateAction<JSX.Element[]>>>(
      UpdaterContext
    );
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Close a specific dialog, resetting
   * certain pieces of state in the process
   *
   * @param selectedDialog selected dialog settings
   */
  const closeDialog = (selectedDialog: IDialog) => {
    setDialogs((dialogs) => {
      return dialogs.filter((dialog) => dialog.key !== selectedDialog.id);
    });
    const currentLocation: RouterLocation = {
      ...location,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash
    };
    const updatedPath = removeSearchParamFromURL(
      currentLocation,
      selectedDialog.name
    );
    /**
     * This check handles a scenario where a user has a Patient View Dialog
     * open, then opens an AddressEvents dialog, and then closes the
     * AddressEvents dialog. The Patient View Dialog is still open, so
     * we don't want to set the URL State to false yet.
     */
    if (!updatedPath.includes('patientId')) {
      urlStateDispatch({
        type: UrlStateActionType.SetToFalse
      });
      // Ensure search string updates too
      if (location.search?.includes('patientId')) {
        const path = updatedPath || `${location.pathname}${location.hash}`;
        navigate(path);
      }
    }
    /**
     * Only push new things onto the history stack if
     * actually warranted (i.e. if the path actually was updated).
     */
    const currentPath = `${currentLocation.pathname}${currentLocation.search}${currentLocation.hash}`;
    const isSamePath = currentPath === updatedPath;
    if (!isSamePath && updatedPath) {
      navigate(updatedPath);
    }
  };

  /**
   * Add dialog component to state
   *
   * @param dialog dialog options
   */
  const addDialog = (dialog: IDialog) => {
    const updatedPath = addSearchParam(location, dialog.urlParam);
    const component: JSX.Element = chooseComponent(dialog, closeDialog);
    setDialogs((dialogs) => {
      return dialogs
        .filter((dialogComponent) => dialogComponent.key !== dialog.id)
        .concat(component);
    });
    const isSamePath = `/${location.search}` === updatedPath;
    if (!isSamePath && updatedPath) {
      navigate(updatedPath);
    }
  };

  return addDialog;
}

export const ActiveDialogs: React.FC = () => {
  const dialogs = useActiveDialogs();
  const addDialog = useActiveDialogsUpdater();
  const urlStateDispatch = useUrlStateUpdater();
  const setDialogs =
    React.useContext<React.Dispatch<React.SetStateAction<JSX.Element[]>>>(
      UpdaterContext
    );
  const { isBackForwardButtonPressed } = useUrlState();
  const location = useLocation();
  const search = location.search;
  const isInitialMountRef = useRef(true);

  /**
   * Based on the URL Search Params, determine
   * what dialogs to open
   *
   * Currently only works with the Patient View Dialog
   */
  const openDialogsBasedOnURLSearchParams = useCallback(() => {
    if (search.length === 0) {
      return;
    }
    // Example search params: ?patientId=2&patientTab=4
    const chunks = search.replace('?', '').split('&');
    const paramGroups: IDialogParamGroups = {
      patient: []
    };
    chunks.forEach((chunk) => {
      if (chunk.includes('patientId') || chunk.includes('patientTab')) {
        paramGroups.patient.push(chunk);
      }
    });
    processParamGroups(paramGroups, addDialog, dialogs);
  }, [search, addDialog, dialogs]);

  /**
   * On component mount, determine if attempting to open
   * Patient View Dialog based on the URL
   */
  useEffect(() => {
    const isViewingPatientView =
      search.length > 0 && search.includes('patientId');

    // Only set isViewingPatientFromUrl on initial mount or back/forward navigation
    if (isInitialMountRef.current) {
      if (isViewingPatientView) {
        openDialogsBasedOnURLSearchParams();
      }
      isInitialMountRef.current = false;
    }
  }, [openDialogsBasedOnURLSearchParams, search]);

  useEffect(() => {
    const handlePopState = () => {
      const hasPatientId =
        window.location.search.length > 0 &&
        window.location.search.includes('patientId');
      urlStateDispatch({
        type: UrlStateActionType.SetIsBackForwardButtonPressed,
        payload: true
      });
      // If back/forward brings us to a URL with patientId, treat as URL-driven
      if (hasPatientId) {
        urlStateDispatch({
          type: UrlStateActionType.SetIsOpenViaUrl,
          payload: true
        });
      } else {
        urlStateDispatch({
          type: UrlStateActionType.SetIsOpenViaUrl,
          payload: false
        });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [urlStateDispatch]);

  /**
   * Handles pressing the back button once to re-open
   * a patient dialog
   */
  useEffect(() => {
    if (isBackForwardButtonPressed) {
      const hasPatientId = search.length > 0 && search.includes('patientId');
      if (hasPatientId) {
        openDialogsBasedOnURLSearchParams();
      }
      // Reset the back/forward flag after handling
      urlStateDispatch({
        type: UrlStateActionType.SetIsBackForwardButtonPressed,
        payload: false
      });
    }
  }, [
    isBackForwardButtonPressed,
    search,
    urlStateDispatch,
    openDialogsBasedOnURLSearchParams
  ]);

  /**
   * Handle normal search param changes (from app-driven dialog opens/closes)
   * Open/close dialogs as needed, but don't change isViewingPatientFromUrl
   * (it should remain false for app-driven opens)
   */
  useEffect(() => {
    if (isInitialMountRef.current) {
      return;
    }

    const isViewingPatientView =
      search.length > 0 && search.includes('patientId');

    // If the URL no longer contains patientId, ensure any view patient dialogs are closed
    if (!isViewingPatientView) {
      const hasViewPatientDialogs = dialogs.some((dialog) =>
        String(dialog.key || '').startsWith('viewPatient')
      );
      if (hasViewPatientDialogs) {
        setDialogs((current) =>
          current.filter(
            (dialog) => !String(dialog.key || '').startsWith('viewPatient')
          )
        );
      }
      return;
    }
    // For app-driven opens/closes, just open/close dialogs without changing isViewingPatientFromUrl
    if (isViewingPatientView) {
      // Check if dialog already exists to avoid duplicates
      const patientIdMatch = search.match(/patientId=(\d+)/);
      if (patientIdMatch) {
        const patientId = patientIdMatch[1];
        const dialogId = `viewPatient${patientId}`;
        const dialogExists = dialogs.some((dialog) => dialog.key === dialogId);
        if (!dialogExists) {
          openDialogsBasedOnURLSearchParams();
        }
      }
    }
  }, [search, dialogs, openDialogsBasedOnURLSearchParams, setDialogs]);

  return <>{dialogs}</>;
};
